import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useAudio } from "../context/AudioContext";

const SVG_W = 960;
const SVG_H = 220;
const MID = SVG_H / 2;

/* Triangle wave: -1 to 1 */
function triangleWave(t) {
  const mod = ((t % 1) + 1) % 1;
  return mod < 0.5 ? mod * 4 - 1 : 3 - mod * 4;
}

/* Build one smooth wave path across double-width for seamless tiling */
function buildWavePath(amplitude, wavelength, phaseShift, shape) {
  const totalW = SVG_W * 2;
  const pts = [];
  const step = 3;
  const p = phaseShift || 0;
  const isTriangle = shape === "triangle";

  for (let x = 0; x <= totalW; x += step) {
    let y;
    if (isTriangle) {
      y =
        MID +
        triangleWave(x / wavelength + p / (Math.PI * 2)) * amplitude * 0.6 +
        triangleWave(x / (wavelength * 2.8) + (p * 0.3) / (Math.PI * 2)) * amplitude * 0.15;
    } else {
      y =
        MID +
        Math.sin((x / wavelength) * Math.PI * 2 + p) * amplitude * 0.45 +
        Math.sin((x / (wavelength * 0.58)) * Math.PI * 2 + p * 1.7 + 1.8) * amplitude * 0.28 +
        Math.sin((x / (wavelength * 2.4)) * Math.PI * 2 + p * 0.5 + 0.6) * amplitude * 0.18;
    }
    pts.push(`${x === 0 ? "M" : "L"}${x} ${y.toFixed(1)}`);
  }
  return pts.join(" ");
}

const LAYERS = [
  { scrollSpeed: 17, opacity: 0.5, strokeWidth: 1.8, ampScale: 1.0, wlScale: 1.0, phaseShift: 0, shape: "sine" },
  { scrollSpeed: 24, opacity: 0.65, strokeWidth: 1.3, ampScale: 0.7, wlScale: 0.78, phaseShift: 2.4, shape: "sine" },
  { scrollSpeed: 32, opacity: 0.8, strokeWidth: 1.0, ampScale: 0.55, wlScale: 1.35, phaseShift: 4.8, shape: "sine" },
  { scrollSpeed: 21, opacity: 0.55, strokeWidth: 1.1, ampScale: 0.6, wlScale: 1.1, phaseShift: 3.5, shape: "triangle" },
  { scrollSpeed: 14, opacity: 0.9, strokeWidth: 2.0, ampScale: 1.2, wlScale: 1.6, phaseShift: 1.2, shape: "sine" },
];

// Minor 9th chord — each voice maps to a visual layer
const CHORD_SEMITONES = [0, 3, 7, 10, 14];
const BASE_FREQ = 130.81; // C3

function semitonesToFreq(base, semitones) {
  return base * Math.pow(2, semitones / 12);
}

export default function SoundPattern() {
  const { getCtx, getMaster } = useAudio();
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const offsetsRef = useRef(LAYERS.map(() => Math.random() * -SVG_W));
  const lastTimeRef = useRef(null);
  const rafRef = useRef(null);
  const synthRef = useRef(null);

  const [amplitude, setAmplitude] = useState(35);
  const [wavelength, setWavelength] = useState(200);
  const [speedMult, setSpeedMult] = useState(1);
  const [offsets, setOffsets] = useState(() => LAYERS.map(() => Math.random() * -SVG_W));
  const [isDragging, setIsDragging] = useState(false);

  /* Each layer gets its own unique shape */
  const wavePaths = useMemo(
    () =>
      LAYERS.map((layer) =>
        buildWavePath(
          amplitude * layer.ampScale,
          wavelength * layer.wlScale,
          layer.phaseShift,
          layer.shape
        )
      ),
    [amplitude, wavelength]
  );

  /* Scroll animation */
  const animate = useCallback(() => {
    const now = performance.now();
    if (lastTimeRef.current === null) lastTimeRef.current = now;
    const dt = (now - lastTimeRef.current) / 1000;
    lastTimeRef.current = now;

    const next = offsetsRef.current.map((offset, i) => {
      const px = LAYERS[i].scrollSpeed * speedMult * dt;
      let o = offset - px;
      if (o <= -SVG_W) o += SVG_W;
      return o;
    });
    offsetsRef.current = next;
    setOffsets([...next]);

    rafRef.current = requestAnimationFrame(animate);
  }, [speedMult]);

  useEffect(() => {
    lastTimeRef.current = null;
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  /* Init synth voices — connected to shared master */
  const initSynth = useCallback(() => {
    if (synthRef.current) return;
    const ctx = getCtx();
    const master = getMaster();

    // Synth-local gain (controls fade in/out on drag)
    const synthGain = ctx.createGain();
    synthGain.gain.value = 0;
    synthGain.connect(master);

    const voices = LAYERS.map((layer, i) => {
      const osc = ctx.createOscillator();
      osc.type = layer.shape === "triangle" ? "triangle" : "sine";
      osc.frequency.value = semitonesToFreq(BASE_FREQ, CHORD_SEMITONES[i]);

      const voiceGain = ctx.createGain();
      const levels = [0.35, 0.25, 0.3, 0.2, 0.15];
      voiceGain.gain.value = levels[i] || 0.2;

      osc.connect(voiceGain);
      voiceGain.connect(synthGain);
      osc.start();

      return { osc, gain: voiceGain };
    });

    synthRef.current = { voices, synthGain };
  }, [getCtx, getMaster]);

  /* Update audio params while dragging */
  useEffect(() => {
    if (!synthRef.current) return;
    const ctx = getCtx();
    const { voices, synthGain } = synthRef.current;
    const t = ctx.currentTime;

    // Wavelength shifts pitch
    const pitchShift = ((200 - wavelength) / 165) * 12;
    voices.forEach((voice, i) => {
      const freq = semitonesToFreq(BASE_FREQ, CHORD_SEMITONES[i] + pitchShift);
      voice.osc.frequency.exponentialRampToValueAtTime(Math.max(freq, 20), t + 0.1);
    });

    // Amplitude → volume, max 0.14
    const vol = isDragging ? Math.min(((amplitude - 6) / 94) * 0.14, 0.14) : 0;
    synthGain.gain.linearRampToValueAtTime(vol, t + 0.1);
  }, [amplitude, wavelength, isDragging, getCtx]);

  /* Pointer handlers */
  const handlePointerDown = useCallback(
    (e) => {
      if (!containerRef.current) return;
      containerRef.current.setPointerCapture(e.pointerId);
      initSynth();

      const rect = containerRef.current.getBoundingClientRect();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        centerX: rect.left + rect.width / 2,
        initAmplitude: amplitude,
        initWavelength: wavelength,
        initSpeed: speedMult,
      };
      setIsDragging(true);
    },
    [amplitude, wavelength, speedMult, initSynth]
  );

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    const dy = e.clientY - d.startY;
    const dx = e.clientX - d.startX;

    setAmplitude(Math.max(6, Math.min(100, d.initAmplitude - dy * 0.4)));
    setWavelength(Math.max(35, Math.min(300, d.initWavelength + dx * 0.5)));

    const distNow = Math.abs(e.clientX - d.centerX);
    const distStart = Math.abs(d.startX - d.centerX) || 150;
    const closeness = 1 - Math.min(distNow / distStart, 1);
    setSpeedMult(Math.max(0.3, Math.min(5, d.initSpeed + closeness * 3)));
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (!containerRef.current) return;
    containerRef.current.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setIsDragging(false);

    // Fade synth out
    if (synthRef.current) {
      const ctx = getCtx();
      synthRef.current.synthGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4);
    }
  }, [getCtx]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (synthRef.current) {
        synthRef.current.voices.forEach((v) => v.osc.stop());
        synthRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`sound-pattern pointer-events-auto w-full select-none touch-none ${
        isDragging ? "cursor-grabbing" : "cursor-grab"
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      role="img"
      aria-label="Interactive waveform — drag to reshape and hear"
    >
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        className="w-full h-auto"
        fill="none"
        aria-hidden="true"
        style={{ overflow: "hidden" }}
      >
        {LAYERS.map((layer, i) => (
          <g
            key={i}
            style={{
              transform: `translateX(${offsets[i]}px)`,
              willChange: "transform",
            }}
          >
            <path
              d={wavePaths[i]}
              stroke="currentColor"
              strokeWidth={layer.strokeWidth}
              strokeLinecap="round"
              opacity={layer.opacity}
            />
          </g>
        ))}
      </svg>
    </div>
  );
}
